import "@livekit/components-styles";
import React from "react";
import {
  ControlBar, GridLayout, LiveKitRoom, ParticipantTile,
  RoomAudioRenderer, StartAudio, useTracks, useConnectionState, useRoomContext,
} from "@livekit/components-react";
import AiParticipant from "./AiParticipant";
import FixedInputDiagnostic from "./FixedInputDiagnostic";
import { Track, ConnectionState } from "livekit-client";

// Real LiveKit media stage for the desktop app. Hosts/cohosts publish per their
// backend-issued grant (can_publish); viewers connect subscribe-only. Screen
// share is disabled by policy. Never renders a "connected" state unless the
// LiveKit room actually reaches ConnectionState.Connected.
export default function LiveStage({ serverUrl, token, canPublish, mediaMode, showAiAvatar = false, aiCharacterIds = ["bruce"], onDisconnected, onConnected, onError, onCount, onMicrophoneReady, fixedInputDiagnostic = false }) {
  const publishVideo = canPublish && mediaMode === "video";
  return (
    <div data-testid="live-stage" className="w-full flex flex-col bg-black rounded-xl">
      <LiveKitRoom
        serverUrl={serverUrl}
        token={token}
        connect
        audio={false}
        video={false}
        onDisconnected={onDisconnected}
        onError={onError}
        options={{ adaptiveStream: true, dynacast: true, publishDefaults: { simulcast: true } }}
        className="flex flex-col"
      >
        <StageState onConnected={onConnected} onCount={onCount} onMicrophoneReady={onMicrophoneReady} />
        {canPublish && fixedInputDiagnostic && <FixedInputDiagnostic enabled />}
        {canPublish ? (
          <div data-testid="live-device-controls" className="shrink-0 border-b border-white/10 bg-[#0d1219]">
            {!publishVideo && <p className="p-3 text-sm text-white/80">This room is audio-only, so camera controls are unavailable here. Choose Human video and audio when creating a private room to use your camera. Devices still start off.</p>}
            {!fixedInputDiagnostic && <MicrophoneConsent />}
            {!fixedInputDiagnostic && publishVideo && <CameraConsent />}
          </div>
        ) : (
          <div className="px-4 py-2 text-[11px] text-white/40 border-t border-white/10 bg-[#0d1219]" data-testid="viewer-subscribe-only">Viewing only — you cannot publish audio or video in this stream.</div>
        )}
        <StageTracks />
        {showAiAvatar && <AiParticipant characterIds={aiCharacterIds} />}
        <RoomAudioRenderer />
        <StartAudio label="Enable room sound" className="border rounded p-2 m-3 text-white" />
      </LiveKitRoom>
    </div>
  );
}

export function MicrophoneConsent({ publishVideo }) {
  const room = useRoomContext();
  const [accepted, setAccepted] = React.useState(false);
  const [enabled, setEnabled] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState("");
  const operating = React.useRef(false);
  const alive = React.useRef(true);
  React.useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);
  async function enable() {
    if (!accepted || operating.current || !room) return;
    operating.current = true; setBusy(true); setError("");
    try {
      await room.localParticipant.setMicrophoneEnabled(true);
      if (alive.current) setEnabled(true);
      else await room.localParticipant.setMicrophoneEnabled(false);
    } catch (_) {
      if (alive.current) setError("Microphone could not be enabled. Check browser permission and your device; no automatic retry was made.");
    } finally { operating.current = false; if (alive.current) setBusy(false); }
  }
  return <div className="p-3 space-y-2 text-sm text-white/80">
    {!enabled && <><p>Your microphone and camera start off. Enabling your microphone shares audio with room participants. If you separately consent to AI and the host starts it, speech is also processed remotely by speech and AI providers.</p>
      <label className="flex gap-2"><input type="checkbox" checked={accepted} onChange={e => setAccepted(e.target.checked)} />I choose to share my microphone audio with this room.</label>
      <button disabled={!accepted || busy} onClick={enable} className="border rounded p-2">Enable my microphone</button></>}
    {error && <p role="alert">{error}</p>}
    <ControlBar variation="minimal" controls={{ microphone: enabled, camera: false, screenShare: false, chat: false, leave: false, settings: false }} />
  </div>;
}

export function CameraConsent() {
  const room = useRoomContext();
  const [accepted, setAccepted] = React.useState(false);
  const [enabled, setEnabled] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState("");
  const operating = React.useRef(false);
  const alive = React.useRef(true);
  React.useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);
  async function enable() {
    if (!accepted || operating.current || !room) return;
    operating.current = true; setBusy(true); setError("");
    try {
      await room.localParticipant.setCameraEnabled(true);
      if (alive.current) setEnabled(true);
      else await room.localParticipant.setCameraEnabled(false);
    } catch (_) {
      if (alive.current) setError("Camera could not be enabled. Check browser permission and your device. No automatic retry was made.");
    } finally { operating.current = false; if (alive.current) setBusy(false); }
  }
  return <div className="p-3 space-y-2 text-sm text-white/80">
    {!enabled && <><p>Your camera starts off. Camera video is shared with room participants. TT does not record this room; the AI co-host processes audio, not your camera images.</p>
      <label className="flex gap-2"><input type="checkbox" checked={accepted} onChange={e => setAccepted(e.target.checked)} />I choose to share my camera video with this room.</label>
      <button disabled={!accepted || busy} onClick={enable} className="border rounded p-2">Enable my camera</button></>}
    {error && <p role="alert">{error}</p>}
    {enabled && <ControlBar variation="minimal" controls={{ microphone: false, camera: true, screenShare: false, chat: false, leave: false, settings: false }} />}
  </div>;
}

export function StageState({ onConnected, onCount, onMicrophoneReady }) {
  const state = useConnectionState();
  const room = useRoomContext();
  React.useEffect(() => {
    if (state === ConnectionState.Connected) onConnected?.();
  }, [state, onConnected]);
  React.useEffect(() => {
    if (!room) return;
    const update = () => onCount?.(room.remoteParticipants.size + 1);
    update();
    room.on("participantConnected", update);
    room.on("participantDisconnected", update);
    return () => { room.off("participantConnected", update); room.off("participantDisconnected", update); };
  }, [room, onCount]);
  React.useEffect(() => {
    const update = () => {
      const pubs = Array.from(room?.localParticipant?.trackPublications?.values?.() || []);
      const mic = pubs.filter(p => p.source === Track.Source.Microphone);
      onMicrophoneReady?.(state === ConnectionState.Connected && mic.length === 1 &&
        !mic[0].isMuted && !!mic[0].track && mic[0].track.mediaStreamTrack?.readyState === 'live');
    };
    const events = ['localTrackPublished','localTrackUnpublished','trackMuted','trackUnmuted','connectionStateChanged'];
    update(); events.forEach(e => room?.on(e, update));
    return () => { events.forEach(e => room?.off(e, update)); onMicrophoneReady?.(false); };
  }, [room, state, onMicrophoneReady]);
  if (state === ConnectionState.Connected) return null;
  const label = state === ConnectionState.Connecting ? "Connecting to live media…"
    : state === ConnectionState.Reconnecting ? "Reconnecting…"
    : state === ConnectionState.Disconnected ? "Disconnected" : "Preparing…";
  return (
    <div className="absolute top-3 left-1/2 -translate-x-1/2 z-10 px-3 py-1 rounded-full bg-black/70 text-white/80 text-xs" data-testid="live-conn-state">{label}</div>
  );
}

function StageTracks() {
  const tracks = useTracks([{ source: Track.Source.Camera, withPlaceholder: true }], { onlySubscribed: false });
  return (
    <div className="relative h-[35vh] min-h-[200px] max-h-[420px]">
      <GridLayout tracks={tracks.filter(t => t.participant.identity !== "tt-ai-rehearsal")} className="h-full">
        <ParticipantTile />
      </GridLayout>
    </div>
  );
}

